/**
 * CloudMap Markers
 * Marker creation and management
 */

// 1/8 mile in degrees (approximately 0.002 degrees)
const OVERLAP_OFFSET = 0.002;
// Threshold for considering markers as overlapping (about 100 meters)
const OVERLAP_THRESHOLD = 0.001;

/**
 * Offset overlapping markers in a circular pattern
 * @param {Array} features - Array of GeoJSON features
 * @returns {Map} Map of feature id to {lat, lng} offset position
 */
export function calculateMarkerOffsets(features) {
    const offsets = new Map();
    const processed = new Set();

    features.forEach((feature, index) => {
        if (processed.has(index)) return;

        const [lng, lat] = feature.geometry.coordinates;
        const overlapping = [];

        // Find all markers at approximately the same location
        features.forEach((other, otherIndex) => {
            if (otherIndex === index) return;
            const [otherLng, otherLat] = other.geometry.coordinates;
            const latDiff = Math.abs(lat - otherLat);
            const lngDiff = Math.abs(lng - otherLng);

            if (latDiff < OVERLAP_THRESHOLD && lngDiff < OVERLAP_THRESHOLD) {
                overlapping.push(otherIndex);
            }
        });

        if (overlapping.length > 0) {
            // Include the current marker in the group
            const group = [index, ...overlapping];

            // Offset each marker in a circular pattern
            group.forEach((featureIndex, i) => {
                processed.add(featureIndex);
                const angle = (2 * Math.PI * i) / group.length;
                const offsetLat = OVERLAP_OFFSET * Math.cos(angle);
                const offsetLng = OVERLAP_OFFSET * Math.sin(angle);
                const [fLng, fLat] = features[featureIndex].geometry.coordinates;

                offsets.set(featureIndex, {
                    lat: fLat + offsetLat,
                    lng: fLng + offsetLng,
                });
            });
        }
    });

    return offsets;
}

/**
 * Create pin SVG for markers
 */
export function createPinSvg(color, opacity = 1) {
    return `
        <svg class="cloudmap-marker-pin" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity: ${opacity}">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 8.25 12 20 12 20s12-11.75 12-20c0-6.627-5.373-12-12-12z" fill="${color}"/>
            <circle cx="12" cy="12" r="5" fill="#fff"/>
        </svg>
    `;
}

/**
 * Create a marker on the map
 * @param {object} options - position, provider, data, map, config, onOpen callback
 */
export function createMarker({position, provider, data, map, config, onOpen}) {
    // Check if this is a planned/coming soon region
    const status = data.status?.toLowerCase() || 'active';
    const isPlanned = status === 'planned' || status === 'coming soon' || status === 'coming-soon';

    // Create marker pin SVG (lighter for planned)
    const pinSvg = createPinSvg(provider.color, isPlanned ? 0.4 : 1);

    // Use Advanced Markers if available (requires Map ID)
    if (google.maps.marker?.AdvancedMarkerElement && config.googleMaps?.mapId) {
        const pinElement = document.createElement('div');
        pinElement.className = 'cloudmap-marker';
        pinElement.innerHTML = pinSvg;
        pinElement.setAttribute('role', 'button');
        pinElement.setAttribute('aria-label', `${data.name} - ${provider.name}`);
        pinElement.setAttribute('tabindex', '0');

        const marker = new google.maps.marker.AdvancedMarkerElement({
            position,
            map: map,
            content: pinElement,
            title: data.name,
        });

        // Click handler
        pinElement.addEventListener('click', () => onOpen(marker, data));
        pinElement.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(marker, data);
            }
        });

        return marker;
    }

    // Fallback to standard markers
    const marker = new google.maps.Marker({
        position,
        map: map,
        title: data.name,
        opacity: isPlanned ? 0.4 : 1,
        icon: {
            path: 'M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z',
            fillColor: provider.color,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 1,
            scale: 1.5,
            anchor: new google.maps.Point(12, 24),
        },
    });

    marker.addListener('click', () => onOpen(marker, data));

    return marker;
}

/**
 * Clear all markers from the map
 */
export function clearMarkers(markers, clusterer) {
    if (clusterer) {
        clusterer.clearMarkers();
    }

    markers.forEach(marker => {
        if (marker.setMap) {
            marker.setMap(null);
        } else if (marker.map) {
            marker.map = null;
        }
    });
}
